import type { FormError } from '#ui/types'
import type { CorpsOption, Option, UnitKerjaOption } from '~/types/form'

interface PersonelForm {
  name: string
  nrp: string
  gender?: Option<string>
  angkatan?: Option<number>
  pangkat?: Option<number>
  corps?: CorpsOption
  unit_kerja?: UnitKerjaOption
  unit_organisasi?: Option<string>
  jabatan?: Option<string>
  unit_kerja_type?: EUnitKerjaType
  unit_organisasi_type?: EUnitKerjaType
  jabatan_type?: EJabatanType
  unit_kerja_custom?: string
  unit_organisasi_custom?: string
  jabatan_custom?: string
}

interface PasswordForm {
  password: string
  confirm_password: string
}

interface EmailForm {
  email: string
}

/**
 * Centralized composable for validating form inputs.
 *
 * This composable provides a collection of validation functions for different form types
 * (e.g., personnel, password, etc.). Each validator accepts the form state and a `Ref<FormError[]>`,
 * then pushes validation messages using the `path` property that matches Nuxt UI FormGroup `name`s.
 *
 * The error messages will appear automatically in the `#error` slot of Nuxt UI FormGroup.
 *
 * You can import this composable and call specific validation functions based on the form you're handling.
 * More validation functions may be added over time as new forms are introduced.
 *
 * @returns An object containing form-specific validation functions.
 */
export const useFormValidation = () => {
  const searchFields = reactive<Record<string, string>>({})

  // Universal handler untuk semua InputSearch
  const onSearchInput = (key: string, e: InputEvent) => {
    const target = e.target as HTMLInputElement
    searchFields[key] = target?.value || ''
  }

  const validatePassword = (state: PasswordForm, errors: Ref<FormError[]>): FormError[] => {
    if (!state.password) {
      errors.value.push({ path: 'password', message: 'Password wajib diisi' })
      return errors.value
    }

    if (state.password?.length < 8) {
      errors.value.push({ path: 'password', message: 'Password minimal 8 karakter' })
    }

    if (state.password.length > 100) {
      errors.value.push({ path: 'password', message: 'Password maksimal 100 karakter' })
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(state.password)) {
      errors.value.push({
        path: 'password',
        message: 'Password harus mengandung huruf besar, huruf kecil, dan angka'
      })
    }

    if (state.password !== state.confirm_password) {
      errors.value.push({ path: 'confirm_password', message: 'Password tidak cocok' })
    }

    return errors.value
  }

  const validateEmail = (state: EmailForm, errors: Ref<FormError[]>): FormError[] => {
    if (!state.email) {
      errors.value.push({ path: 'email', message: 'Email wajib diisi' })
      return errors.value
    }
    if (state.email.length < 5) {
      errors.value.push({ path: 'email', message: 'Email minimal 5 karakter' })
    }
    if (state.email.length > 100) {
      errors.value.push({ path: 'email', message: 'Email maksimal 100 karakter' })
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(state.email)) {
      errors.value.push({ path: 'email', message: 'Email tidak valid' })
    }

    return errors.value
  }

  const validatePersonel = (
    state: PersonelForm,
    errors: Ref<FormError[]>,
    isTNI: boolean
  ): FormError[] => {
    if (!state.name) {
      errors.value.push({
        path: 'name',
        message: 'Anda wajib menginputkan nama'
      })
    } else {
      if (state.name.length < 3) {
        errors.value.push({
          path: 'name',
          message: 'Nama minimal 3 karakter'
        })
      }

      if (state.name.length > 100) {
        errors.value.push({
          path: 'name',
          message: 'Nama maksimal 100 karakter'
        })
      }

      if (/[#$%&*=;!?/]/.test(state.name)) {
        errors.value.push({
          path: 'name',
          message: 'Nama tidak boleh mengandung simbol #, $, %, &, *, =, ;, !, ?, / atau emoji'
        })
      }

      if (
        /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}]/u.test(
          state.name
        )
      ) {
        errors.value.push({
          path: 'name',
          message: 'Nama tidak boleh mengandung emoji'
        })
      }
    }

    if (!state.gender) errors.value.push({ path: 'gender', message: 'Jenis kelamin wajib diisi' })
    if (!state.nrp) errors.value.push({ path: 'nrp', message: 'Anda wajib menginputkan NRP/NIP' })
    if (state.nrp) {
      if (state.nrp.length > 24) {
        errors.value.push({ path: 'nrp', message: 'NRP/NIP tidak boleh melebihi 24 karakter' })
      }
      if (!/^\d+(?:\/P)?$/i.test(state.nrp)) {
        errors.value.push({
          path: 'nrp',
          message: 'NRP/NIP hanya boleh mengandung angka atau /P dibelakang'
        })
      }
    }

    const angkatanText = (searchFields.angkatan || '').trim()
    if (!angkatanText && !state.angkatan) {
      errors.value.push({ path: 'angkatan', message: 'Angkatan wajib diisi' })
    } else if (angkatanText && !state.angkatan) {
      errors.value.push({
        path: 'angkatan',
        message: 'Pilih opsi yang sesuai'
      })
    }

    const pangkatText = (searchFields.pangkat || '').trim()
    if (!pangkatText && !state.pangkat) {
      errors.value.push({ path: 'pangkat', message: 'Pangkat wajib diisi' })
    } else if (pangkatText && !state.pangkat) {
      errors.value.push({
        path: 'pangkat',
        message: 'Pilih opsi yang sesuai'
      })
    }

    if (isTNI) {
      const corpsText = (searchFields.corps || '').trim()

      if (!corpsText && !state.corps) {
        errors.value.push({ path: 'corps', message: 'Corps wajib diisi' })
      } else if (corpsText && !state.corps) {
        errors.value.push({
          path: 'corps',
          message: 'Pilih opsi yang sesuai'
        })
      }
    }

    if (state.unit_kerja_type === EUnitKerjaType.SIMPEG && !state.unit_kerja) {
      const text = (searchFields.unit_kerja || '').trim()
      if (!text && !state.unit_kerja) {
        errors.value.push({ path: 'unit_kerja', message: 'Unit kerja wajib diisi' })
      } else if (text && !state.unit_kerja) {
        errors.value.push({
          path: 'unit_kerja',
          message: 'Pilih Tipe Unit Kerja "Lain-lain" untuk input manual'
        })
      }
    } else if (state.unit_kerja_type === EUnitKerjaType.CUSTOM) {
      if (!state.unit_kerja_custom) {
        errors.value.push({ path: 'unit_kerja_custom', message: 'Unit kerja wajib diisi' })
      } else {
        if (state.unit_kerja_custom.length < 3) {
          errors.value.push({ path: 'unit_kerja_custom', message: 'Unit kerja minimal 3 karakter' })
        }
        if (state.unit_kerja_custom.length > 100) {
          errors.value.push({
            path: 'unit_kerja_custom',
            message: 'Unit kerja maksimal 100 karakter'
          })
        }
        const regex = /^[A-Za-zÀ-ÿ.,\s]+$/
        if (!regex.test(state.unit_kerja_custom)) {
          errors.value.push({
            path: 'unit_kerja_custom',
            message: 'Unit kerja hanya boleh berisi huruf, titik, spasi, atau koma'
          })
        }
      }
    }

    // Jabatan
    if (state.jabatan_type === EJabatanType.KEPALA && !state.jabatan) {
      const text = (searchFields.jabatan_kepala || '').trim()
      if (!text && !state.jabatan) {
        errors.value.push({ path: 'jabatan_kepala', message: 'Jabatan Kepala wajib diisi' })
      } else if (text && !state.jabatan) {
        errors.value.push({
          path: 'jabatan_kepala',
          message: 'Pilih Tipe Jabatan "Lain-lain" untuk input manual'
        })
      }
    } else if (state.jabatan_type === EJabatanType.FUNGSIONAL && !state.jabatan) {
      const text = (searchFields.jabatan || '').trim()
      if (!text && !state.jabatan) {
        errors.value.push({ path: 'jabatan', message: 'Jabatan wajib diisi' })
      } else if (text && !state.jabatan) {
        errors.value.push({
          path: 'jabatan',
          message: 'Pilih Tipe Jabatan "Lain-lain" untuk input manual'
        })
      }
    } else if (state.jabatan_type === EJabatanType.CUSTOM) {
      if (!state.jabatan_custom) {
        errors.value.push({ path: 'jabatan_custom', message: 'Jabatan custom wajib diisi' })
      } else {
        if (state.jabatan_custom.length < 3) {
          errors.value.push({
            path: 'jabatan_custom',
            message: 'Jabatan custom minimal 3 karakter'
          })
        }
        if (state.jabatan_custom.length > 100) {
          errors.value.push({
            path: 'jabatan_custom',
            message: 'Jabatan custom maksimal 100 karakter'
          })
        }
        const regex = /^[A-Za-zÀ-ÿ.,\s]+$/
        if (!regex.test(state.jabatan_custom)) {
          errors.value.push({
            path: 'jabatan_custom',
            message: 'Jabatan custom hanya boleh berisi huruf, titik, spasi, atau koma'
          })
        }
      }
    }

    // Unit Organisasi
    if (state.unit_kerja_type !== EUnitKerjaType.SIMPEG) {
      if (state.unit_organisasi_type === EUnitKerjaType.SIMPEG && !state.unit_organisasi) {
        const text = (searchFields.unit_organisasi || '').trim()
        if (!text && !state.unit_organisasi) {
          errors.value.push({ path: 'unit_organisasi', message: 'Unit organisasi wajib diisi' })
        } else if (text && !state.unit_organisasi) {
          errors.value.push({
            path: 'unit_organisasi',
            message: 'Pilih Tipe Unit Organisasi "Lain-lain" untuk input manual'
          })
        }
      } else if (state.unit_organisasi_type === EUnitKerjaType.CUSTOM) {
        if (!state.unit_organisasi_custom) {
          errors.value.push({
            path: 'unit_organisasi_custom',
            message: 'Unit organisasi wajib diisi'
          })
        } else {
          if (state.unit_organisasi_custom.length < 3) {
            errors.value.push({
              path: 'unit_organisasi_custom',
              message: 'Unit organisasi minimal 3 karakter'
            })
          }
          if (state.unit_organisasi_custom.length > 100) {
            errors.value.push({
              path: 'unit_organisasi_custom',
              message: 'Unit organisasi maksimal 100 karakter'
            })
          }
          const regex = /^[A-Za-zÀ-ÿ.,\s]+$/
          if (!regex.test(state.unit_organisasi_custom)) {
            errors.value.push({
              path: 'unit_organisasi_custom',
              message: 'Unit Organisasi hanya boleh berisi huruf, titik, spasi, atau koma'
            })
          }
        }
      }
    }

    return errors.value
  }

  return {
    validateEmail,
    validatePassword,
    validatePersonel,
    onSearchInput
  }
}
